import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { groupBy } from 'lodash-es';
import { releaseDateFormatter } from './utils';
import igdbIcon from '@intelligence/assets/svg/gameInfo/igdb.svg';
import { getPlatformIcons } from '@intelligence/utils/common';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import MoreReleaseDateModal from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/ReleaseDate/MoreReleaseDateModal';
import {
  BACKUP_SHOW_PLATFORM_LIST,
  FIXED_SHOW_PLATFORM_LIST,
  HIDE_PLATFORM_LIST,
  PLATFORM_ORDER,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/ReleaseDate/_config';
import otherPlatformIcon from '@/pages/intelligence/assets/svg/dataSource/other-platform.svg';
import './index.scss';

interface IReleaseInfo {
  status?: string;
  country?: string;
  originStatus?: string;
  release_date?: string;
  iconComponent?: React.ReactNode;
}

export interface IReleaseDateByPlatformData {
  platform: string;
  release_info: IReleaseInfo;
}

type TFlattedReleaseDateData = { platform: string; icon: string } & IReleaseInfo;

export interface IReleaseDateByPlatformNewProps {
  isRelease: boolean;
  list: IReleaseDateByPlatformData;
}

const ReleaseDate: React.FC<IReleaseDateByPlatformNewProps> = (props) => {
  const { isRelease, list } = props;
  const [showMore, setShowMore] = useState(false);

  const emptyTipsLocaleKey = isRelease
    ? 'dbi_detail_release_date_by_platform_nodata_tips'
    : 'dbi_detail_game_not_launch_tips';

  const { formatMessage, isEN } = useIntlExtend();

  // 中英切换显示
  const formattedList = useMemo(() => {
    // 中英切换展示并保留原始对应字段，后续排序使用
    const listTmp = releaseDateFormatter?.(list, formatMessage) || list;
    return Array.isArray(listTmp) ? listTmp : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, isEN]);

  // 数据打平
  const flattedList = useMemo(() => {
    const res: TFlattedReleaseDateData[] = [];
    formattedList
      // 排除不展示 platform
      ?.filter((item) => !HIDE_PLATFORM_LIST.includes(item.platform))
      // 数据打平并指定图标
      .forEach((item) => {
        item.release_info.forEach((subItem: IReleaseInfo) => {
          const isMacOrLinux = ['linux'].includes(item?.platform?.toLowerCase());
          const icon = getPlatformIcons(isMacOrLinux ? 'pc' : item.platform) || otherPlatformIcon;
          res.push({
            ...subItem,
            platform: item.platform,
            icon,
          });
        });
      });
    return res;
  }, [formattedList]);

  // 固定展的的 full release、early access 对应平台数据
  const {
    fixedShowFullReleaseList,
    fixedShowEarlyAccessList,
    fixedShowAlphaList,
    fixedShowBetaList,
    fixedShowReleasedList,
    fixedShowCancelledList,
    fixedShowOfflineList,
  } = useMemo(() => {
    // const fullRelease: TFlattedReleaseDateData[] = [];
    // const earlyAccess: TFlattedReleaseDateData[] = [];

    const sortedList = flattedList
      ?.slice()
      ?.sort((a, b) => moment(a.release_date).valueOf() - moment(b.release_date).valueOf());

    // console.log('---  sortedList   ---\n', sortedList);

    const statusMap = groupBy(sortedList, 'originStatus');

    // console.log('---  statusMap  ---\n', statusMap);

    // 取没阶段当前平台发布最早的阶段
    Object.keys(statusMap)?.forEach((status) => {
      const currentStatusList = statusMap[status];

      const res = [];
      const existedPlatforms: string[] = [];
      currentStatusList?.forEach((item) => {
        if (!existedPlatforms?.includes(item.platform)) {
          existedPlatforms.push(item.platform);
          res.push(item);
        }
        // existedPlatforms
      });

      // 按平添排序并截取前 6 项
      res
        ?.sort((a, b) => {
          const aIndex = PLATFORM_ORDER.findIndex((item) => item === a.platform);
          const bIndex = PLATFORM_ORDER.findIndex((item) => item === b.platform);
          return bIndex - aIndex;
        })
        ?.slice(0, 6);

      // console.log('---  res  ---\n', res);

      statusMap[status] = res;
    });

    // const getFixedShowData = (platforms: string[]) => {
    //   platforms?.forEach((platform) => {
    //     // 排序保证先取到最早的 full release、early access 的 release_date

    //     console.log('---  sortedList  ---\n', sortedList);
    //     // const statusMap = sortedList?.reduce((prev,curr)=>{},{})

    //     const currentPlatformFullReleaseData = sortedList?.find((item) => {
    //       return (
    //         item.platform === platform && item.originStatus?.toLocaleLowerCase() === 'full release'
    //       );
    //     });
    //     const currentPlatformEarlyAccessData = sortedList?.find((item) => {
    //       return (
    //         item.platform === platform && item.originStatus?.toLocaleLowerCase() === 'early access'
    //       );
    //     });

    //     currentPlatformFullReleaseData && fullRelease.push(currentPlatformFullReleaseData);
    //     currentPlatformEarlyAccessData && earlyAccess.push(currentPlatformEarlyAccessData);
    //   });
    // };

    // getFixedShowData(FIXED_SHOW_PLATFORM_LIST);

    // // 如果固定展示的平台都没有获取到对应数据，但是表格中有其他数据，取备用展示信息
    // if (flattedList?.length && !fullRelease?.length && !earlyAccess?.length) {
    //   getFixedShowData(BACKUP_SHOW_PLATFORM_LIST);
    // }

    // return {
    //   fixedShowFullReleaseList: fullRelease,
    //   fixedShowEarlyAccessList: earlyAccess,
    // };

    // TODO_NOTE: confirm 这个处理方式不对

    // console.log('statusMapstatusMap', statusMap);
    return {
      fixedShowFullReleaseList: statusMap['Full Release'],
      fixedShowEarlyAccessList: statusMap['Early Access'],
      fixedShowAlphaList: statusMap.Alpha,
      fixedShowBetaList: statusMap.Beta,
      fixedShowReleasedList: statusMap.Released,
      fixedShowCancelledList: statusMap.Cancelled,
      fixedShowOfflineList: statusMap.Offline,
    };
  }, [flattedList]);

  // 展示顺序及展示数量控制
  const fixedRenderInfo = useMemo(() => {
    // 单一一项固定平台展示不足 6 条时，先用此项其他发布平台补全 6 条展示，如果当前项发布平台不足 6 项，使用其余发布状态补齐展示
    // 两个类型都不足 6 条时展示全部

    const fullReleaseLength = fixedShowFullReleaseList?.length || 0;
    const alphaLength = fixedShowAlphaList?.length || 0;
    const betaLength = fixedShowBetaList?.length || 0;
    const releasedLength = fixedShowReleasedList?.length || 0;
    const offlineLength = fixedShowOfflineList?.length || 0;

    // 获取同平台下，最早时间的那条
    const getEarlysData = (listData: any) => {
      listData?.sort((a: any, b: any) => {
        const isAvalueInvalid = !!(!a.release_date || a.release_date === '');
        const isBvalueInvalid = !!(!b.release_date || b.release_date === '');
        if (isAvalueInvalid && isBvalueInvalid) return 0;
        if (isAvalueInvalid) return 1;
        if (isBvalueInvalid) return -1;
        return moment(b.release_date).valueOf() - moment(a.release_date).valueOf();
      });

      // listData?.sort((a, b) => moment(b.release_date).valueOf() - moment(a.release_date).valueOf());
      return Array.isArray(listData) && listData?.length > 0 ? listData[0]?.release_date : ''; // 数组的第一个元素是最晚的
    };

    // 未上线游戏，按时间降序排序
    const getNoReleaseSortList = (list: any) => {
      const sortReleaseData = list?.sort((a: any, b: any) => {
        const isAvalueInvalid = !!(!a.release_date || a.release_date === '');
        const isBvalueInvalid = !!(!b.release_date || b.release_date === '');
        if (isAvalueInvalid && isBvalueInvalid) return 0;
        if (isAvalueInvalid) return 1;
        if (isBvalueInvalid) return -1;
        return moment(b.release_date).valueOf() - moment(a.release_date).valueOf();
      });
      // const sortReleaseData = list?.sort(
      //   (a: any, b: any) => new Date(b.release_date) - new Date(a.release_date),
      // );
      const resultList: any = [];
      let num = 0;
      sortReleaseData?.forEach((itemObj: any) => {
        if (num < 6) {
          const subObjValue = {
            text: itemObj.text,
            data: itemObj.list,
          };
          resultList.push(subObjValue);
          num = num + itemObj?.list?.length;
        }
      });
      return resultList;
    };

    return isRelease
      ? [
          {
            text: 'dbi_off_line',
            data: fixedShowOfflineList,
          },
          // 第一项不足 6 条时才展示后面的选项
          ...(offlineLength < 6
            ? [
                {
                  text: 'dbi_full_release',
                  // 第二项展示的条数加上第一项的总条数展示 6 条
                  data: fixedShowFullReleaseList?.slice(0, 6 - offlineLength),
                },
              ]
            : []),
          ...(offlineLength + fullReleaseLength < 6
            ? [
                {
                  text: 'dbi_released',
                  data: fixedShowReleasedList?.slice(0, 6 - (offlineLength + fullReleaseLength)),
                },
              ]
            : []),
          ...(offlineLength + fullReleaseLength + releasedLength < 6
            ? [
                {
                  text: 'dbi_alpha',
                  data: fixedShowAlphaList?.slice(
                    0,
                    6 - (offlineLength + fullReleaseLength + releasedLength),
                  ),
                },
              ]
            : []),
          ...(offlineLength + fullReleaseLength + releasedLength + alphaLength < 6
            ? [
                {
                  text: 'dbi_beta',
                  data: fixedShowBetaList?.slice(
                    0,
                    6 - (offlineLength + fullReleaseLength + releasedLength + alphaLength),
                  ),
                },
              ]
            : []),
          ...(offlineLength + fullReleaseLength + releasedLength + alphaLength + betaLength < 6
            ? [
                {
                  text: 'dbi_early_access',
                  data: fixedShowEarlyAccessList?.slice(
                    0,
                    6 -
                      (offlineLength +
                        fullReleaseLength +
                        releasedLength +
                        alphaLength +
                        betaLength),
                  ),
                },
              ]
            : []),
        ]
      : getNoReleaseSortList([
          {
            type: 'Full Release',
            text: 'dbi_full_release',
            list: fixedShowFullReleaseList,
            release_date: getEarlysData(fixedShowFullReleaseList),
          },
          {
            type: 'Early Access',
            text: 'dbi_early_access',
            list: fixedShowEarlyAccessList,
            release_date: getEarlysData(fixedShowEarlyAccessList),
          },
          {
            type: 'Alpha',
            text: 'dbi_alpha',
            list: fixedShowAlphaList,
            release_date: getEarlysData(fixedShowAlphaList),
          },
          {
            type: 'Beta',
            text: 'dbi_beta',
            list: fixedShowBetaList,
            release_date: getEarlysData(fixedShowBetaList),
          },
          {
            type: 'Released',
            text: 'dbi_released',
            list: fixedShowReleasedList,
            release_date: getEarlysData(fixedShowReleasedList),
          },
          {
            type: 'Cancelled',
            text: 'dbi_cancelled',
            list: fixedShowCancelledList,
            release_date: getEarlysData(fixedShowCancelledList),
          },
          {
            type: 'Offline',
            text: 'dbi_off_line',
            list: fixedShowOfflineList,
            release_date: getEarlysData(fixedShowOfflineList),
          },
        ]);
  }, [
    isRelease,
    fixedShowFullReleaseList,
    fixedShowEarlyAccessList,
    fixedShowAlphaList,
    fixedShowBetaList,
    fixedShowReleasedList,
    fixedShowCancelledList,
    fixedShowOfflineList,
  ]);

  // console.log('---  fixedRenderInfo  ---\n', fixedRenderInfo);

  // More 按钮展示条件：固定展示条数 < 总条数
  const showHasMoreBtn = useMemo(() => {
    const fixedShowLength = fixedRenderInfo?.reduce(
      (prev, curr) => prev + (curr?.data?.length || 0),
      0,
    );
    // console.log('---  fixedShowLength  ---\n', fixedShowLength);

    return flattedList?.length !== fixedShowLength;
  }, [flattedList, fixedRenderInfo]);

  // 渲染列表
  const renderList = (data: TFlattedReleaseDateData[]) =>
    data?.map((item: TFlattedReleaseDateData, index) => {
      return (
        <div className="dbi-release-date-item" key={index}>
          <div className="dbi-release-date-item-platform">
            <img src={item.icon} alt="" />
            <div>{item.platform}</div>
          </div>
          <div className="dbi-release-date-item-date">{item.release_date || 'TBD'}</div>
        </div>
      );
    });

  // 固定展示列表渲染
  const renderContent = () => {
    // 无数据
    if (!flattedList?.length) return <span>{formatMessage({ id: emptyTipsLocaleKey })}</span>;

    return (
      <div className="dbi-release-date-context">
        {fixedRenderInfo?.map(
          (item) =>
            // 对应类型有数据才展示
            Boolean(item.data?.length) && (
              <>
                {/* 类型小标题 */}
                <div className="dbi-release-date-type">{formatMessage({ id: item.text })}</div>

                {/* 展示对应类型的列表 */}
                {renderList(item.data)}
              </>
            ),
        )}
      </div>
    );
  };

  return (
    <>
      <div className="dbi-release-date-wrapper">
        <div className="dbi-release-date">
          <div className="dbi-release-date-title">
            <div className="dbi-release-date-title-left">
              <img className="dbi-release-date-title-img" src={igdbIcon}></img>
              {formatMessage({ id: 'dbi_release_date_full_release' })}
            </div>

            {showHasMoreBtn && (
              <div className="dbi-release-date-title-right" onClick={() => setShowMore(true)}>
                {formatMessage({ id: 'dbi_more' })}
              </div>
            )}
          </div>

          {renderContent()}
        </div>
      </div>

      {/* 展示更多弹窗 */}
      <MoreReleaseDateModal
        visible={showMore}
        setVisible={setShowMore}
        isRelease={isRelease}
        data={flattedList}
      />
    </>
  );
};

export default ReleaseDate;
